import { ChevronRight, Clock, FileText } from 'lucide-react';

import { Card } from '@/components/ui/card';

import { FolderData } from './types';

interface Props {
    folder: FolderData;
    navigateToFolder: (letter: string) => void;
}

export function IATAFolderCard({ folder, navigateToFolder }: Props) {
    // Status-based left border color
    const borderColor =
        folder.priority === 'high' ? 'border-l-rose-500' : 'border-l-emerald-500';

    // Subtle background tint for high priority
    const bgTint = folder.priority === 'high' ? 'bg-rose-50/50' : '';

    // Letter badge border color - matches card status with low opacity
    const badgeBorder =
        folder.priority === 'high' ? 'border-rose-500/25' : 'border-emerald-500/25';

    return (
        <Card
            onClick={() => navigateToFolder(folder.letter)}
            className={`group p-4 hover:shadow-lg hover:border-primary/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer flex flex-col h-full border-l-4 ${borderColor} ${bgTint}`}
            data-testid={`IATAFolderCard__${folder.letter}`}
        >
            {/* Header Row: Letter badge + Title + Chevron */}
            <div className="flex justify-between items-start gap-2 mb-2">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                        className={`h-9 w-9 rounded-md bg-white border ${badgeBorder} flex items-center justify-center flex-shrink-0`}
                    >
                        <span className="text-sm font-bold text-foreground/70">
                            {folder.letter}
                        </span>
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-foreground text-sm leading-tight group-hover:text-primary transition-colors">
                            {folder.name}
                        </h3>
                        <span className="text-xs text-muted-foreground">
                            {folder.assignee}
                        </span>
                    </div>
                </div>
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-muted/50 group-hover:bg-primary/10 transition-colors flex-shrink-0">
                    <ChevronRight
                        size={14}
                        className="text-muted-foreground group-hover:text-primary transition-colors"
                    />
                </div>
            </div>

            {/* Metrics Row - compact */}
            <div className="mt-auto flex items-center gap-3 text-xs text-muted-foreground">
                <div
                    className={`flex items-center gap-1 ${folder.documents === 0 ? 'text-muted-foreground/60' : ''}`}
                >
                    <FileText size={12} />
                    <span className="font-medium">{folder.documents}</span>
                </div>
                <div
                    className={`flex items-center gap-1 ${folder.lastUpdate === 'No activity' ? 'text-muted-foreground/60' : ''}`}
                >
                    <Clock size={12} />
                    <span>{folder.lastUpdate}</span>
                </div>

                {/* Progress - only show if > 0% */}
                {folder.progress > 0 && (
                    <div className="ml-auto flex items-center gap-1.5">
                        <div className="w-12 bg-muted rounded-full h-1.5">
                            <div
                                className={`h-1.5 rounded-full transition-all duration-500 ${folder.priority === 'high' ? 'bg-rose-500' : 'bg-primary'}`}
                                style={{ width: `${folder.progress}%` }}
                            />
                        </div>
                        <span className={`text-[10px] font-semibold ${folder.priority === 'high' ? 'text-rose-500' : 'text-primary'}`}>
                            {folder.progress}%
                        </span>
                    </div>
                )}
            </div>
        </Card>
    );
}
